import {Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {DeploymentEnvironment} from '@service-and-repairs/awpintegrationlib';

/**
 * Service that provides configuration URLs for the Retail Companion widget
 * (`rcom-chat-widget`) and loads the CDN-based widget loader.
 *
 * The widget contract follows the CDN integration model while still keeping
 * AWP-owned proxy paths during the migration.
 *
 * The base URL is the current window origin for deployed environments. When
 * running locally (DEV_LOCAL) the dev-test deployment is used instead, since
 * there is no backend on `localhost`.
 *
 * @see https://atc-github.azure.cloud.bmw/dcomp/frontend
 * @see https://atc.bmwgroup.net/confluence/spaces/RPAS/pages/6397208529/Retailer+Companion+Widget+Integration
 */
@Injectable({
  providedIn: 'root'
})
export class AwpAgentService {
  private readonly devLoaderBaseUrl: string = 'https://retailer-companion-dev.bmw.cloud/loader.js';
  private readonly intLoaderBaseUrl: string = 'https://retailer-companion-int.bmw.cloud/loader.js';
  private readonly prodLoaderBaseUrl: string = 'https://retailer-companion.bmw.cloud/loader.js';
  private readonly backendApiBasePath: string = '/api/ai/retail-companion-backend/api/v1';

  /**
   * Base URL of the AWP dev-test deployment. Used as the widget API host when
   * running locally (DEV_LOCAL) so the widget targets the real dev-test
   * backend/proxies instead of `localhost`, which has no backend.
   */
  private readonly devTestBaseUrl: string = 'https://awp-test.bmwgroup.com';

  loadWidgetLoader(): Promise<void> {
    return new Promise<void>((resolve: () => void, reject: (reason?: Error) => void): void => {
      const targetDocument: Document = document;
      const loaderScriptUrl: string = this.getLoaderScriptUrl();

      if (targetDocument.querySelector(`script[src="${loaderScriptUrl}"]`)) {
        resolve();
      } else if (targetDocument.head) {
        const script: HTMLScriptElement = targetDocument.createElement('script');
        // The CDN integration requires a module loader script with the current
        // application hostname propagated via `?app=...`.
        script.type = 'module';
        script.async = true;
        script.src = loaderScriptUrl;
        script.onload = (): void => {
          resolve();
        };
        script.onerror = (): void => {
          script.parentElement?.removeChild(script);
          reject(new Error(`AwpAgentService: Failed to load Retail Companion loader ${script.src}`));
        };
        targetDocument.head.appendChild(script);
      } else {
        reject(new Error(`AwpAgentService: Failed to attach Retail Companion loader ${loaderScriptUrl} as target document has no head.`));
      }
    });
  }

  getLoaderScriptUrl(): string {
    const loaderScriptUrl: URL = new URL(this.getLoaderBaseUrl());
    loaderScriptUrl.searchParams.set('app', window.location.hostname);
    return loaderScriptUrl.toString();
  }

  private getLoaderBaseUrl(): string {
    switch (environment.deploymentEnvironment) {
      case DeploymentEnvironment.DEV_LOCAL:
      case DeploymentEnvironment.DEV_TEST:
        return this.devLoaderBaseUrl;
      case DeploymentEnvironment.INT:
      case DeploymentEnvironment.QA:
      case DeploymentEnvironment.QA_US:
      case DeploymentEnvironment.QA_CN:
        return this.intLoaderBaseUrl;
      case DeploymentEnvironment.PROD:
      case DeploymentEnvironment.PROD_US:
      case DeploymentEnvironment.PROD_CN:
        return this.prodLoaderBaseUrl;
      default:
        throw new Error(`Unsupported deployment environment for Retail Companion loader: ${environment.deploymentEnvironment}`);
    }
  }

  /**
   * Returns the full base URL for the RC Backend, proxied through the AWP backend.
   */
  getBackendApiBaseUrl(): string {
    return this.getBaseUrl() + this.backendApiBasePath;
  }

  /**
   * Returns the base URL used for the widget API calls.
   *
   * - When running locally (DEV_LOCAL) there is no local backend, so the
   *   dev-test deployment (`https://awp-test.bmwgroup.com`) is used instead of
   *   `localhost`.
   * - For deployed environments `window.location.origin` is used so requests
   *   stay same-origin and flow through the AWP reverse proxy. `origin`
   *   preserves the port; using `protocol + hostname` would drop it and send
   *   requests to port 80, causing `ERR_CONNECTION_REFUSED`.
   */
  private getBaseUrl(): string {
    if (environment.deploymentEnvironment === DeploymentEnvironment.DEV_LOCAL) {
      return this.devTestBaseUrl;
    }
    return window.location.origin;
  }
}
